import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { api } from './api';

const POLL_MS = 5000;

// Server state for the page. Polls while the tab is visible so the other
// person's changes show up within a few seconds; every write refreshes too.
// Only server-owned data lives here -- anything half-typed stays in the
// component that owns the input, so a refresh never clobbers it.
export default function useTrackerData() {
  const [data, setData] = useState(null);
  const [sync, setSync] = useState('loading'); // loading | synced | syncing | offline
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSync((s) => (s === 'loading' ? 'loading' : 'syncing'));
    try {
      const payload = await api.load();
      setData(payload);
      setSync('synced');
    } catch (e) {
      setSync('offline');
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    refresh();
    const tick = () => { if (document.visibilityState === 'visible') refresh(); };
    const timer = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [refresh]);

  // Merge one task from a write response so the UI moves before the next poll.
  const putTask = useCallback((task) => {
    setData((d) => {
      if (!d) return d;
      const known = d.tasks.some((t) => t.id === task.id);
      return { ...d, tasks: known ? d.tasks.map((t) => (t.id === task.id ? task : t)) : [...d.tasks, task] };
    });
  }, []);
  const dropTask = useCallback((id) => setData((d) => d && { ...d, tasks: d.tasks.filter((t) => t.id !== id) }), []);
  const putEvent = useCallback((event) => {
    setData((d) => {
      if (!d) return d;
      const known = d.events.some((e) => e.id === event.id);
      return { ...d, events: known ? d.events.map((e) => (e.id === event.id ? event : e)) : [...d.events, event] };
    });
  }, []);
  const dropEvent = useCallback((id) => setData((d) => d && { ...d, events: d.events.filter((e) => e.id !== id) }), []);

  return {
    data, sync, refresh, putTask, dropTask, putEvent, dropEvent, setData,
  };
}
